import { CloseOutlined } from '@ant-design/icons'
import { useGoogleLogin } from '@react-oauth/google'
import { Button, Col, Drawer, Form, message, Modal, Row, Space } from 'antd'
import axios from 'axios'
import promiseRetry from 'promise-retry'
import { useEffect, useRef, useState } from 'react'
import AppleLogin from 'react-apple-login'
import { useHistory, useLocation } from 'react-router'
import config from '../../config'
import { login } from '../../contexts/Auth'
import { useLoading } from '../../contexts/LoadingContext'
import { appleLogin } from '../../services/auth/apple.service'
import { LoginOTPVerifySuccess } from '../../services/auth/auth.interfaces'
import { googleLogin } from '../../services/auth/google-auth.service'
import {
  instanceOfGetOTPSuccess,
  instanceOfLoginSuccess,
  instanceOfMobileDuplicated,
  instanceOfMobileRequiredToRegister,
  instanceOfRequireMobile,
  instanceOfRequireOTP,
} from '../../services/auth/instanceof.function'
import {
  loginByCredential,
  loginVerifyMobileOtp,
  userLoginGetMobileOTP,
} from '../../services/auth/login.service'
import { userRegisterGetMobileOtp } from '../../services/auth/register.service'
import MixpanelTracker from '../../services/track/mixpanel'
import * as image from '../../static/img/login-page'
import { AFFILIATE_CODE_QUERY_PARAM, buildShareCarPath } from '../../utils/affiliate-code'
import { errorMessage } from '../../utils/function.errorMessage'
import { useArrayWatch } from '../../utils/useArrayWatch'
import { popResetPassword } from '../Modal/ResetPasswordModal'
import { Heading2 } from '../StyledComponents/StyledComponents'
import { LoginDrawerProps } from './CustomDrawer.interfaces'
import { CustomLoginInputPasswordV2, CustomLoginInputV2 } from './Elements'
import { popMobileDrawer } from './MobileDrawer'
import {
  popOTPDrawer,
  popRetryableLoginVerifyOtp,
  popRetryableRegisterVerifyOtp,
} from './OTPDrawer'
import { showApiErrorModal } from '../../utils/apiErrorModal'

export const LoginDrawer = (props: LoginDrawerProps) => {
  const onClose = props.onClose ? props.onClose : () => null
  const [email, password] = useArrayWatch<[string, string]>(['email', 'password'], props.formRef)
  const loginCredentialComplete = Boolean(email && password)
  const history = useHistory()
  const location = useLocation()
  const mobileDrawerRef = useRef<HTMLDivElement>(null)
  const otpDrawerRef = useRef<HTMLDivElement>(null)
  const { setLoading } = useLoading()
  const [socialLoginKey, setSocialLoginKey] = useState(0)
  const [googleLoginReady, setGoogleLoginReady] = useState(false)

  const handleGoogleClick = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Use access token to get user profile
        const profileResponse = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        })
        const profileObj = profileResponse.data

        if (!profileObj || !profileObj.email) {
          return message.error('Failed to get Google profile')
        }

        // For backend authentication, we need to get an ID token
        // The backend expects an id_token to validate with Google
        const tokenInfoResponse = await axios.get(
          `https://oauth2.googleapis.com/tokeninfo?access_token=${tokenResponse.access_token}`,
        )

        // Use the access token as the authentication token
        // The backend will validate it against Google's tokeninfo endpoint
        const loginResponse = await googleLogin(tokenResponse.access_token)
        await handleGoogleLoginFlow(loginResponse, profileObj, tokenResponse.access_token)
      } catch (error: any) {
        console.error('Google login error:', error)
        message.error(error.message || 'Google login failed')
      }
    },
    onError: () => {
      message.error('Google login failed')
    },
  })

  useEffect(() => {
    if (props.open) {
      // Force re-render of Google login button to reset OAuth state
      setSocialLoginKey((prev) => prev + 1)
      // Small delay to ensure cleanup happens
      setGoogleLoginReady(false)
      const timer = setTimeout(() => {
        setGoogleLoginReady(true)
      }, 100)
      return () => clearTimeout(timer)
    } else {
      setGoogleLoginReady(false)
    }
  }, [props.open])

  function redirectToSubmission() {
    const postLoginRedirect = localStorage.getItem('postLoginRedirect')
    if (postLoginRedirect) {
      localStorage.removeItem('postLoginRedirect')
      history.replace(postLoginRedirect)
      return
    }

    const locationState = location.state as { from?: string }
    const queryParams = new URLSearchParams(location.search)
    const requestId = queryParams.get('request_id')

    if (requestId) {
      history.replace(buildShareCarPath(requestId, queryParams.get(AFFILIATE_CODE_QUERY_PARAM)))
      return
    }

    const redirectPath = locationState?.from || '/submission'
    history.replace(redirectPath)
  }

  const loginWithTracking = async (
    accessToken: string,
    userData: any,
    redirectFn: () => void,
    source = 'Email',
    isNewUser = false,
  ) => {
    MixpanelTracker.track(isNewUser ? 'Register Success' : 'Login Success', {
      source: source,
      'User ID': userData.userid,
      Email: userData.username,
      'Registration Method': source,
      'User Name': userData.name + userData.lastname,
      'Login Type': isNewUser ? 'New User' : 'Returning User',
      Timestamp: new Date().toISOString(),
    })

    MixpanelTracker.identify(userData.userid)

    return await login(accessToken, userData, redirectFn)
  }

  function parseJwt(token: string) {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Error parsing JWT:', error)
      return null
    }
  }

  async function handleGoogleLoginFlow(
    loginResponse: any,
    profileObj: any,
    idToken: string,
  ): Promise<void> {
    const socialSource = 'Google'

    if (instanceOfLoginSuccess(loginResponse)) {
      return await loginWithTracking(
        loginResponse.accessToken,
        loginResponse.userData,
        redirectToSubmission,
        socialSource,
        loginResponse.isNewUser ?? false,
      )
    }
    if (instanceOfRequireMobile(loginResponse)) {
      const personalInfo = await popMobileDrawer(
        mobileDrawerRef.current,
        profileObj.given_name,
        profileObj.family_name,
        profileObj.email,
        undefined,
        undefined,
        profileObj.picture,
        loginResponse.loginToken,
      )
      if (!personalInfo) {
        return
      }
      setLoading(true)
      const requestOTP = await userLoginGetMobileOTP(
        Number(personalInfo.mobilePrefix),
        personalInfo.mobile,
        loginResponse.loginToken,
        personalInfo.email,
        personalInfo.firstName,
        personalInfo.lastName,
        personalInfo.photoUrl,
      ).finally(() => setLoading(false))
      if (instanceOfMobileDuplicated(requestOTP)) {
        return showApiErrorModal(requestOTP)
      }
      if (instanceOfGetOTPSuccess(requestOTP)) {
        if (!otpDrawerRef.current) {
          throw new Error('OTP drawer ref is not available')
        }
        const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
          mobile: requestOTP.mobile,
          refCode: requestOTP.refCode,
        }).catch((error: any) => {
          message.error(error)
          throw error
        })
        if (!(loginSuccess.accessToken && loginSuccess.userData)) {
          throw 'Invalid Login Data'
        }
        return loginWithTracking(
          loginSuccess.accessToken,
          loginSuccess.userData,
          redirectToSubmission,
          socialSource,
        )
      }
    }
    if (instanceOfRequireOTP(loginResponse)) {
      if (!otpDrawerRef.current) {
        throw 'OTP Drawer ref is not available'
      }
      const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
        mobile: loginResponse.otp.mobile,
        refCode: loginResponse.otp.refCode,
      }).catch((error: any) => {
        message.error(error)
        throw error
      })
      if (!(loginSuccess.accessToken && loginSuccess.userData)) {
        throw 'Invalid Login Data'
      }
      return loginWithTracking(
        loginSuccess.accessToken,
        loginSuccess.userData,
        redirectToSubmission,
        socialSource,
      )
    }
    if (instanceOfMobileRequiredToRegister(loginResponse)) {
      const registerResponse = loginResponse as any
      const personalInfo = await popMobileDrawer(
        mobileDrawerRef.current,
        profileObj.given_name,
        profileObj.family_name,
        profileObj.email,
        undefined,
        undefined,
        profileObj.picture,
        registerResponse.token,
      )
      if (!personalInfo) {
        return
      }
      setLoading(true)
      const requestOTP = await userRegisterGetMobileOtp(
        Number(personalInfo.mobilePrefix),
        personalInfo.mobile,
        personalInfo.email,
        personalInfo.firstName,
        personalInfo.lastName,
        personalInfo.photoUrl,
      ).finally(() => setLoading(false))
      if (instanceOfMobileDuplicated(requestOTP)) {
        return showApiErrorModal(requestOTP)
      }
      if (!otpDrawerRef.current) {
        throw 'OTP Drawer ref is not available'
      }
      const registerOtpValid = await popRetryableRegisterVerifyOtp(otpDrawerRef.current, {
        mobile: requestOTP.mobile,
        refCode: requestOTP.refCode,
      }).catch((error: any) => {
        message.error(error)
        throw error
      })
      const finalLoginResponse = await googleLogin(idToken, registerOtpValid.token)
      if (instanceOfRequireMobile(finalLoginResponse)) {
        return message.error(finalLoginResponse.message)
      }
      if (instanceOfRequireOTP(finalLoginResponse)) {
        return message.error(finalLoginResponse.message)
      }
      return loginWithTracking(
        finalLoginResponse.accessToken,
        finalLoginResponse.userData,
        redirectToSubmission,
        socialSource,
        true,
      )
    }
  }

  async function handleSocialLoginSuccess(res: any, channel: string): Promise<void> {
    const loginResponse = res
    const socialSource = channel === 'APPLE' ? 'Apple ID' : 'Google'

    if (instanceOfLoginSuccess(loginResponse)) {
      return await loginWithTracking(
        loginResponse.accessToken,
        loginResponse.userData,
        redirectToSubmission,
        socialSource,
        loginResponse.isNewUser ?? false,
      )
    }
    if (instanceOfRequireMobile(loginResponse)) {
      const personalInfo = await popMobileDrawer(
        mobileDrawerRef.current,
        loginResponse.firstName,
        loginResponse.lastName,
        loginResponse.email,
        undefined,
        undefined,
        loginResponse.photoUrl,
        loginResponse.loginToken,
      )
      if (!personalInfo) {
        return
      }
      setLoading(true)
      const requestOTP = await userLoginGetMobileOTP(
        Number(personalInfo.mobilePrefix),
        personalInfo.mobile,
        loginResponse.loginToken,
        personalInfo.email,
        personalInfo.firstName,
        personalInfo.lastName,
        personalInfo.photoUrl,
      ).finally(() => setLoading(false))
      if (instanceOfMobileDuplicated(requestOTP)) {
        return showApiErrorModal(requestOTP)
      }
      if (instanceOfGetOTPSuccess(requestOTP)) {
        if (!otpDrawerRef.current) {
          throw 'OTP Drawer ref is not available'
        }
        const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
          mobile: requestOTP.mobile,
          refCode: requestOTP.refCode,
        }).catch((error: any) => {
          message.error(error)
          throw error
        })
        if (!(loginSuccess.accessToken && loginSuccess.userData)) {
          throw 'Invalid Login Data'
        }
        return loginWithTracking(
          loginSuccess.accessToken,
          loginSuccess.userData,
          redirectToSubmission,
          socialSource,
        )
      }
    }
    if (instanceOfRequireOTP(loginResponse)) {
      if (!otpDrawerRef.current) {
        throw 'OTP Drawer ref is not available'
      }
      const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
        mobile: loginResponse.otp.mobile,
        refCode: loginResponse.otp.refCode,
      }).catch((error: any) => {
        message.error(error)
        throw error
      })
      if (!(loginSuccess.accessToken && loginSuccess.userData)) {
        throw 'Invalid Login Data'
      }
      return loginWithTracking(
        loginSuccess.accessToken,
        loginSuccess.userData,
        redirectToSubmission,
        socialSource,
      )
    }
    if (instanceOfMobileRequiredToRegister(loginResponse)) {
      const registerResponse = loginResponse as any // Type narrowing workaround
      const personalInfo = await popMobileDrawer(
        mobileDrawerRef.current,
        registerResponse.firstName,
        registerResponse.lastName,
        registerResponse.email,
        undefined,
        undefined,
        registerResponse.photoUrl,
        registerResponse.token, // Use token field for registration flow
      )
      if (!personalInfo) {
        return
      }
      setLoading(true)
      const requestOTP = await userRegisterGetMobileOtp(
        Number(personalInfo.mobilePrefix),
        personalInfo.mobile,
        personalInfo.email,
        personalInfo.firstName,
        personalInfo.lastName,
        personalInfo.photoUrl,
      ).finally(() => setLoading(false))
      if (instanceOfMobileDuplicated(requestOTP)) {
        return showApiErrorModal(requestOTP)
      }
      if (!otpDrawerRef.current) {
        throw 'OTP Drawer ref is not available'
      }
      const registerOtpValid = await popRetryableRegisterVerifyOtp(otpDrawerRef.current, {
        mobile: requestOTP.mobile,
        refCode: requestOTP.refCode,
      }).catch((error: any) => {
        message.error(error)
        throw error
      })
      const finalLoginResponse =
        channel === 'APPLE'
          ? await appleLogin(res.tokenId, registerOtpValid.token)
          : await googleLogin(res.tokenId, registerOtpValid.token)
      if (instanceOfRequireMobile(finalLoginResponse)) {
        return message.error(finalLoginResponse.message)
      }
      if (instanceOfRequireOTP(finalLoginResponse)) {
        return message.error(finalLoginResponse.message)
      }
      return loginWithTracking(
        finalLoginResponse.accessToken,
        finalLoginResponse.userData,
        redirectToSubmission,
        socialSource,
        true,
      )
    }
  }

  async function loginViaCredential() {
    if (!email || !password) {
      return message.error('Email and Password Required.')
    }
    try {
      setLoading(true)
      const loginResult = await loginByCredential(email, password).finally(() => setLoading(false))
      if (instanceOfLoginSuccess(loginResult)) {
        return loginWithTracking(
          loginResult.accessToken,
          loginResult.userData,
          redirectToSubmission,
        )
      }
      if (instanceOfRequireOTP(loginResult)) {
        if (!otpDrawerRef.current) {
          throw 'OTP Drawer ref is not available'
        }
        const { mobile, refCode, expireAt } = loginResult.otp
        const loginSuccess = await promiseRetry<LoginOTPVerifySuccess>(
          async (retry) => {
            const otpValue = await popOTPDrawer(otpDrawerRef.current!, {
              otpLength: 4,
              title: 'Login',
              mobileNumber: mobile,
              refCode: refCode,
            })
            if (otpValue === '') {
              throw 'Cancel'
            }
            const otpResult = await loginVerifyMobileOtp(refCode, otpValue).catch((error) => {
              console.error(error.message)
              message.error(error)
              return retry(error)
            })
            if (!otpResult.accessToken || !otpResult.userData) {
              retry('Invalid OTP Data')
            }
            return otpResult
          },
          { retries: 3, minTimeout: 0 },
        ).catch((error: any) => message.error(error))

        if (!(loginSuccess.result === true && loginSuccess.accessToken && loginSuccess.userData)) {
          throw 'Invalid Login Data'
        }
        return loginWithTracking(
          loginSuccess.accessToken,
          loginSuccess.userData,
          redirectToSubmission,
        )
      }
      if (instanceOfRequireMobile(loginResult)) {
        const personalInfo = await popMobileDrawer(
          mobileDrawerRef.current,
          loginResult.firstName,
          loginResult.lastName,
          loginResult.email,
          undefined,
          undefined,
          loginResult.photoUrl,
          loginResult.loginToken,
        )
        if (!personalInfo) {
          return
        }
        setLoading(true)
        const requestOTP = await userLoginGetMobileOTP(
          Number(personalInfo.mobilePrefix),
          personalInfo.mobile,
          loginResult.loginToken,
          personalInfo.email,
          personalInfo.firstName,
          personalInfo.lastName,
          personalInfo.photoUrl,
        ).finally(() => setLoading(false))
        if (instanceOfMobileDuplicated(requestOTP)) {
          return showApiErrorModal(requestOTP)
        }
        if (instanceOfGetOTPSuccess(requestOTP)) {
          // get otp ok
          if (!otpDrawerRef.current) {
            throw 'OTP Drawer ref is not available'
          }
          const loginSuccess = await promiseRetry<LoginOTPVerifySuccess>(
            async (retry) => {
              const otpValue = await popOTPDrawer(otpDrawerRef.current!, {
                otpLength: 4,
                title: 'Login',
                mobileNumber: requestOTP.mobile,
                refCode: requestOTP.refCode,
              })
              if (otpValue === '') {
                throw 'Cancel'
              }
              const otpResult = await loginVerifyMobileOtp(requestOTP.refCode, otpValue).catch(
                (error) => {
                  console.error(error.message)
                  message.error(error)
                  return retry(error)
                },
              )
              if (!otpResult.accessToken || !otpResult.userData) {
                return retry('Invalid OTP Data')
              }
              return otpResult
            },
            { retries: 3, minTimeout: 0 },
          ).catch((error: any) => message.error(error))

          if (loginSuccess.result === true && loginSuccess.accessToken && loginSuccess.userData) {
            return loginWithTracking(
              loginSuccess.accessToken,
              loginSuccess.userData,
              redirectToSubmission,
            )
          }
          throw loginSuccess
        }
        throw requestOTP
      }
      throw loginResult
    } catch (error: any) {
      console.error(error)
      const message = errorMessage(error)
      if (message) {
        Modal.error({ content: errorMessage(error) })
      }
    }
  }

  return (
    <>
      <Drawer
        open={props.open}
        onClose={() => onClose()}
        placement="bottom"
        getContainer={false}
        height="90%"
        headerStyle={{
          borderBottom: 0,
          zIndex: 1 /* boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.25)' */,
        }}
        title={
          <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
            <Col span={2}></Col>
            <Col span={20} style={{ textAlign: 'center' }}>
              <Heading2 style={{ color: '#0048B3' }}>Login</Heading2>
            </Col>
            <Col span={2}>
              <CloseOutlined
                style={{ fontSize: '20px', color: '#00000050' }}
                onClick={() => onClose()}
              />
            </Col>
          </Row>
        }
        contentWrapperStyle={{
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden',
        }}
        drawerStyle={{ background: '#fff' }}
        closeIcon={null}
        // maskStyle={{ background: 'transparent' }}
        bodyStyle={{ position: 'relative' }}
        style={{
          maxWidth: '500px',
          margin: '0 auto',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Form
            form={props.formRef}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                loginViaCredential()
              }
            }}
          >
            <Form.Item
              name="email"
              rules={[
                { type: 'email', message: 'Only email' },
                { required: true, message: 'ห้ามเว้นว่าง' },
              ]}
            >
              <CustomLoginInputV2 placeholder="Email" />
            </Form.Item>
            <Form.Item>
              <Form.Item
                noStyle
                name="password"
                rules={[{ required: true, message: 'ห้ามเว้นว่าง' }]}
              >
                <CustomLoginInputPasswordV2 placeholder="Password" />
              </Form.Item>
              <a onClick={popResetPassword} style={{ float: 'right' }}>
                Forgot password ?
              </a>
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                shape="round"
                size="large"
                disabled={!loginCredentialComplete}
                onClick={loginViaCredential}
                style={{
                  backgroundColor: '#0048B3',
                  borderColor: '#0048B3',
                  color: '#fff',
                  height: '48px',
                }}
              >
                Login
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <p className="text-center" style={{ color: '#667085', marginBottom: '12px' }}>
                Or log in with or others
              </p>
              <div className="text-center">
                <Space size="large" align="center">
                  <AppleLogin
                    key={`apple-${socialLoginKey}`}
                    clientId={config.social.APPLE_LOGIN_ID}
                    redirectURI={process.env.REACT_APP_APPLE_LOGIN_CALLBACK_URL ?? ''}
                    responseMode="form_post"
                    scope="email name"
                    usePopup={false}
                    callback={(response: any) => {
                      if (response.authorization) {
                        handleSocialLoginSuccess(response, 'APPLE')
                      }
                    }}
                    render={(renderProps) => (
                      <div
                        onClick={renderProps.onClick}
                        style={{
                          width: '56px',
                          height: '56px',
                          backgroundColor: '#F4F5F9',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <img
                          src={image.apple2x}
                          srcSet={`${image.apple2x} 2x`}
                          style={{ width: '32px', height: '32px' }}
                        />
                      </div>
                    )}
                  />
                  {googleLoginReady && (
                    <div
                      key={`google-${socialLoginKey}`}
                      onClick={() => handleGoogleClick()}
                      style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: '#F4F5F9',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <img
                        src={image.google}
                        srcSet={`${image.google2x} 2x`}
                        style={{ width: '32px', height: '32px' }}
                      />
                    </div>
                  )}
                  {!googleLoginReady && (
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: '#F4F5F9',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    >
                      <img
                        src={image.google}
                        srcSet={`${image.google2x} 2x`}
                        style={{ width: '32px', height: '32px' }}
                      />
                    </div>
                  )}
                </Space>
              </div>
            </Form.Item>
          </Form>
        </Space>
      </Drawer>
      <div ref={mobileDrawerRef} />
      <div ref={otpDrawerRef} />
    </>
  )
}
